import {format} from 'date-fns';
import {Hide, Show, Text} from 'platform/foundation';
import {css, useTheme} from 'styled-components';

import {pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

import type {BigCalendarEvent} from '../types/BigCalendarEvent';
import {getThemeToken} from '../utils/getThemeToken';
import {isMultiDayEvent} from '../utils/isMultiDayEvent';
import {repeatingGradient} from '../utils/repeatingGradient';

interface MonthEventItemProps extends RequiredTestIdProps {
  event: BigCalendarEvent;
  onClick?: (event: BigCalendarEvent) => void;
  enableHover?: boolean;
  onMouseEnter?: (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => void;
  onMouseLeave?: (event: BigCalendarEvent) => void;
}

export function MonthEventItem(props: MonthEventItemProps) {
  const theme = useTheme();
  const EVENT_COLOR = props.event.color || 'general.black';
  const EVENT_COLOR_HEX = getThemeToken(theme, `colors.${EVENT_COLOR}`);

  const isMultiDay = isMultiDayEvent(props.event.start, props.event.end);

  const onClick = () => {
    if (props.event.isReadOnly) {
      return;
    }

    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_month_event_click',
      value: {
        eventId: props.event.id,
      },
    });

    props.onClick?.(props.event);
  };

  const onMouseEnter = (mouseEvent: React.MouseEvent) => {
    props.onMouseEnter?.(props.event, mouseEvent);
  };

  const onMouseLeave = () => {
    props.onMouseLeave?.(props.event);
  };

  return (
    <div
      data-testid={suffixTestId('monthEventItem', props)}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      css={css`
        cursor: pointer;
        display: flex;
        flex-direction: row;
        gap: 4px;
        background-color: ${props.event.alternative ? 'transparent' : EVENT_COLOR_HEX};
        background-image: ${props.event.alternative ? repeatingGradient(EVENT_COLOR_HEX) : 'none'};
        border: ${props.event.alternative ? `1px solid ${EVENT_COLOR_HEX}` : 'none'};
        border-radius: ${theme.radii.small};
        padding: 2px 4px;
        opacity: 0.9;
        transition:
          opacity 0.2s ease,
          filter 0.2s ease;

        ${props.enableHover
          ? `
          &:hover {
            opacity: 1;
            filter: brightness(0.85);
          }
        `
          : ''}
      `}
    >
      <Hide when={props.event.isAllDay || isMultiDay}>
        <Text
          data-testid={suffixTestId('monthEventItem-time', props)}
          size="xSmall"
          color={props.event.alternative ? 'primary' : 'white'}
        >
          <Show when={isMultiDay}>
            {`${format(props.event.start, 'd.M H:mm')} - ${format(props.event.end, 'd.M H:mm')}`}
          </Show>
          <Show when={!isMultiDay}>{format(props.event.start, 'H:mm')}</Show>
        </Text>
      </Hide>
      <Text
        data-testid={suffixTestId('monthEventItem-title', props)}
        size="xSmall"
        color={props.event.alternative ? 'primary' : 'white'}
        noWrap
        alternative
      >
        {props.event.title}
      </Text>
    </div>
  );
}
